import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { DividerModule } from 'primeng/divider';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TagModule } from 'primeng/tag';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { BillingService } from '../../services/billing.service';
import { PropertyPricingTierDetails } from '../../services/response/PropertyPricingTierDetails';
import { PropertyStoreService } from '../../services/stores/property-store.service';
import { RouterLink } from '@angular/router';
import { environment } from '../../../environments/environment';
import { UserPermissions } from '../../domain/security/user-permissions';
import { PermissionsStoreService } from '../../services/stores/permission-store-service';

export type TierDisplayMode = 'groups' | 'ai-tasks' | 'both';

const BILLING_PAGE_LINK = environment.supportLinks.billingPage;

@Component({
   selector: 'app-current-pricing-tier',
   standalone: true,
   imports: [
      CommonModule,
      DividerModule,
      ProgressSpinnerModule,
      TagModule,
      RouterLink
   ],
   templateUrl: './current-pricing-tier.component.html',
   styleUrls: ['./current-pricing-tier.component.scss']
})
export class CurrentPricingTierComponent implements OnInit, OnDestroy {
   @Input() displayMode: TierDisplayMode = 'both';

   loading = false;
   tier: PropertyPricingTierDetails | null = null;
   billingPageLink = BILLING_PAGE_LINK;

   perms = new UserPermissions();
   permsLoaded = false;

   private destroy$ = new Subject<void>();
   private _tierReqSub?: Subscription;

   constructor(
      private billing: BillingService,
      private propStore: PropertyStoreService,
      private permissionsStore: PermissionsStoreService
   ) {}

   ngOnInit(): void {
      this.permissionsStore
         .get()
         .pipe(takeUntil(this.destroy$))
         .subscribe((p) => {
            this.perms = p ?? new UserPermissions();
            this.permsLoaded = true;
         });

      this.propStore.currentProperty
         .pipe(takeUntil(this.destroy$))
         .subscribe((property) => {
            if (!property || property.id == null) return;

            this._tierReqSub?.unsubscribe();

            this.loading = true;
            this._tierReqSub = this.billing.getPricingTier(property.id).subscribe({
               next: (res) => {
                  this.tier = res;
                  this.loading = false;
               },
               error: () => {
                  this.tier = null;
                  this.loading = false;
               }
            });
         });
   }

   get showGroups() {
      return this.displayMode === 'groups' || this.displayMode === 'both';
   }
   get showAiTasks() {
      return this.displayMode === 'ai-tasks' || this.displayMode === 'both';
   }

   get canBillingPage() {
      return this.perms.isAccountOwner;
   }

   ngOnDestroy(): void {
      this._tierReqSub?.unsubscribe();
      this.destroy$.next();
      this.destroy$.complete();
   }
}
